import { AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
export const FONTS = [
  { label: 'Classic serif', css: 'Georgia, "Times New Roman", serif' },
  { label: 'Clean sans', css: 'Arial, Helvetica, sans-serif' },
  { label: 'Bold headline', css: 'Impact, "Arial Black", sans-serif' },
  { label: 'Typewriter', css: '"Courier New", Courier, monospace' },
  { label: 'Script', css: '"Brush Script MT", "Segoe Script", cursive' },
];
const SIZES = [10, 14, 18, 24, 36, 48, 72]; // points

export type Align = 'left' | 'center' | 'right';
export interface TextStyle { text: string; font: number; pt: number; bold: boolean; align: Align }
interface Props { style: TextStyle; onChange: (s: TextStyle) => void; editing: boolean; onDone: () => void; onDelete: () => void }

const sel = 'h-11 border border-input bg-background px-2 text-sm';

/** Words + font/size/bold controls for the sketch box. Edits the selected text when one is picked. */
export function SketchTextPanel({ style, onChange, editing, onDone, onDelete }: Props) {
  const set = (p: Partial<TextStyle>) => onChange({ ...style, ...p });
  return (
    <div className="mt-3 space-y-2 border border-border bg-background p-3">
      <label htmlFor="sk-text" className="block text-sm font-medium">
        {editing ? 'Editing the selected text. Changes show right away.' : 'Step 1: Type your words here'}
      </label>
      <textarea id="sk-text" value={style.text} onChange={(e) => set({ text: e.target.value })} rows={2} className="w-full border border-input bg-background p-2 text-sm" placeholder="e.g. GRAND OPENING" />
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="sk-font" className="sr-only">Font</label>
        <select id="sk-font" value={style.font} onChange={(e) => set({ font: Number(e.target.value) })} className={sel}>
          {FONTS.map((f, i) => <option key={f.label} value={i} style={{ fontFamily: f.css }}>{f.label}</option>)}
        </select>
        <label htmlFor="sk-size" className="sr-only">Size</label>
        <select id="sk-size" value={style.pt} onChange={(e) => set({ pt: Number(e.target.value) })} className={sel}>
          {SIZES.map((s) => <option key={s} value={s}>{s} pt</option>)}
        </select>
        <button type="button" aria-pressed={style.bold} onClick={() => set({ bold: !style.bold })}
          className={`inline-flex min-h-11 items-center border px-3 text-xs font-bold ${style.bold ? 'border-foreground bg-foreground text-background' : 'border-border bg-background'}`}>B Bold</button>
        <fieldset className="inline-flex border border-border"><legend className="sr-only">Line up text</legend>
          {([['left', AlignLeft, 'Flush left'], ['center', AlignCenter, 'Center'], ['right', AlignRight, 'Flush right']] as const).map(([a, Icon, label]) => (
            <button key={a} type="button" aria-label={label} title={label} aria-pressed={style.align === a} onClick={() => set({ align: a })}
              className={`flex h-11 w-11 items-center justify-center ${style.align === a ? 'bg-foreground text-background' : 'bg-background'}`}><Icon className="h-4 w-4" /></button>
          ))}
        </fieldset>
        {editing && <>
          <button type="button" onClick={onDone} className="min-h-11 border border-foreground px-3 text-xs font-semibold">Done, add new text</button>
          <button type="button" onClick={onDelete} className="min-h-11 px-3 text-xs font-semibold text-destructive">Delete</button>
        </>}
      </div>
      {!editing && (
        <ol className="space-y-1 border-l-4 border-[hsl(var(--brand-blue))] bg-muted/50 py-2 pl-3 pr-2 text-sm">
          <li><strong>Step 2:</strong> Pick the font, size, bold, and center, flush left or flush right.</li>
          <li><strong>Step 3:</strong> When you've finished typing, <strong>tap inside the ad box</strong> below to place your words.</li>
          <li><strong>Step 4:</strong> Press and <strong>drag the words</strong> to move them where you want.</li>
        </ol>
      )}
      <p className="text-xs text-muted-foreground">To change words already in the box, tap them and then tap the blue pencil, or double-click them, and type right there. Sizes match the printed ad.</p>
    </div>
  );
}
